import React, { useCallback } from 'react';
import type { FC } from 'react';
import { Box, useInput } from '@wix/cli-ui-kit';
import { match, lookup } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import { CloseScreen } from '../CloseScreen/CloseScreen.js';
import { useBackendComponents } from '../../backend/useBackendComponents.js';
import {
  NumericOption,
  NumericViewer,
} from '../NumericViewer/NumericViewer.js';
import { BackendScreenSelector } from './BackendScreen.js';
import { useWixCliAppBi } from '../../bi/index.js';
import type { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';
import type {
  BackendViewerComponent,
  BackendViewerComponentNames,
} from './backend-viewer.js';

const MAX_BACKEND_COMPONENT = 9;
const MAX_SERVICE_PLUGINS = 4;
const MAX_EVENTS = 5;

interface Props {
  onViewMore: (backendScreen: BackendScreenSelector) => void;
  onSelect?: (backendComponent: BackendViewerComponent) => void;
  onClose?: () => void;
}

export const BackendNumericViewer: FC<Props> = ({
  onViewMore,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation();
  const bi = useWixCliAppBi();
  const { backendComponentsByType } = useBackendComponents();

  const servicePluginsData = backendComponentsByType.ServicePlugin.slice(
    0,
    backendComponentsByType.ServicePlugin.length > MAX_SERVICE_PLUGINS
      ? MAX_SERVICE_PLUGINS - 1
      : backendComponentsByType.ServicePlugin.length
  );

  const servicePluginsShowMoreIndex =
    backendComponentsByType.ServicePlugin.length > servicePluginsData.length
      ? servicePluginsData.length + 1
      : undefined;

  const servicePluginsCount = Math.max(
    servicePluginsData.length,
    servicePluginsShowMoreIndex ?? 0
  );

  const maxBackendEvents = Math.min(
    MAX_EVENTS,
    MAX_BACKEND_COMPONENT - servicePluginsCount
  );

  const backendEventsData = backendComponentsByType.Event.slice(
    0,
    backendComponentsByType.Event.length > maxBackendEvents
      ? maxBackendEvents - 1
      : backendComponentsByType.Event.length
  );

  const backendEventsShowMoreIndex =
    backendComponentsByType.Event.length > backendEventsData.length
      ? backendEventsData.length + servicePluginsCount + 1
      : undefined;

  const visibleItems: Array<
    NumericOption<BackendViewerComponent, BackendViewerComponentNames>
  > = [
    ...servicePluginsData.map(
      (servicePlugin: DevCenterComponent<'ServicePlugin'>) =>
        NumericOption.Item({
          title: servicePlugin.servicePlugin.name,
          value: servicePlugin,
        })
    ),
    ...(servicePluginsShowMoreIndex
      ? [NumericOption.ViewMore('ServicePlugin' as BackendViewerComponentNames)]
      : []),
    ...backendEventsData.map((event: DevCenterComponent<'Event'>) =>
      NumericOption.Item({
        title: event.title,
        value: event,
      })
    ),
    ...(backendEventsShowMoreIndex
      ? [NumericOption.ViewMore('Event' as BackendViewerComponentNames)]
      : []),
  ];

  const getLabelKey = (backendComponent: BackendViewerComponent) => {
    return match(
      backendComponent,
      lookup({
        Api: 'general.backend_api_viewer.title',
        Event: 'general.backend_events_viewer.title',
        ServicePlugin: 'general.backend_plugin_viewer.title',
      })
    );
  };

  useInput((input) => {
    const inputNumber = Number(input);
    if (inputNumber > 0 && inputNumber <= visibleItems.length) {
      const item = visibleItems.at(inputNumber - 1);
      if (item) {
        match(item, {
          Item: (item) => {
            bi.cliAction({
              question: t(getLabelKey(item.value)),
              questionKey: getLabelKey(item.value),
              action: item.title,
              key: input,
            });
            handleSelect(item.value);
          },
          ViewMore: (viewMore) => {
            bi.cliAction({
              question: t('general.numeric_viewer.show_more'),
              questionKey: 'general.numeric_viewer.show_more',
              action: 'view_more',
              key: input,
            });
            onViewMore(
              BackendScreenSelector.Arrow({
                backendComponentName: viewMore.payload,
              })
            );
          },
        });
      }
    }
  });

  const handleSelect = useCallback(
    (backendComponent: BackendViewerComponent) => {
      onSelect?.(backendComponent);
    },
    [onSelect]
  );

  const backendPlugins = visibleItems.slice(0, servicePluginsCount);
  const backendEvents = visibleItems.slice(servicePluginsCount);

  return (
    <Box flexDirection="column" gap={1}>
      {backendPlugins.length > 0 && (
        <NumericViewer
          startIndex={0}
          titleKey="general.backend_plugin_viewer.title"
          items={backendPlugins}
        />
      )}
      {backendEvents.length > 0 && (
        <NumericViewer
          startIndex={servicePluginsCount}
          titleKey="general.backend_events_viewer.title"
          items={backendEvents}
        />
      )}
      {onClose && <CloseScreen onClose={onClose} />}
    </Box>
  );
};
